import React, { useState, useEffect } from 'react';
import {
  Save,
  Loader2,
  FileText,
  AlertCircle,
  Upload,
  Download,
  ArrowLeft
} from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const AdmissionTestEdit = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testData, setTestData] = useState({
    enabled: true,
    mandatory: false,
    registrationOpen: true,
    testName: '',
    testDate: '',
    testTime: '',
    duration: '',
    syllabus: '',
    examPattern: '',
    totalMarks: '',
    passingMarks: '',
    instructions: '',
    registrationFee: 0,
    lastDateToRegister: ''
  });
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);

  const isNewTest = testId === 'new';

  useEffect(() => {
    if (!isNewTest && testId) {
      loadTestConfiguration();
      loadRegistrations();
    } else if (isNewTest) {
      setLoading(false);
    }
  }, [testId]);

  const loadTestConfiguration = async () => {
    try {
      setLoading(true);
      const result = await admissionsService.getAdmissionTestById(testId);
      if (result.success && result.test) {
        setTestData(result.test);
      } else {
        toast.error('Test not found');
        navigate('/coaching-admin/admissions/tests');
      }
    } catch (error) {
      toast.error('Error loading test configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrations = async () => {
    if (!testId || testId === 'new') {
      return;
    }
    
    try {
      setLoadingRegistrations(true);
      console.log('Loading registrations for testId:', testId);
      const result = await admissionsService.getTestRegistrations(testId);
      console.log('Registrations result:', result);
      if (result.success) {
        setRegistrations(result.registrations || []);
      } else {
        console.error('Failed to load registrations:', result.error);
        toast.error('Failed to load registrations: ' + result.error);
      }
    } catch (error) {
      console.error('Error loading registrations:', error);
      toast.error('Error loading registrations: ' + error.message);
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const handleInputChange = (field, value) => {
    setTestData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!testData.testName || testData.testName.trim() === '') {
      toast.error('Please enter test name');
      return;
    }
    if (!testData.testDate) {
      toast.error('Please select test date');
      return;
    }

    try {
      setSaving(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      if (!institutionId) {
        toast.error('Institution not found. Please save your profile first.');
        return;
      }

      const result = await admissionsService.saveAdmissionTest(
        institutionId,
        testData,
        isNewTest ? null : testId
      );
      
      if (result.success) {
        toast.success(isNewTest ? 'Test created successfully' : 'Test updated successfully');
        if (isNewTest) {
          navigate(`/coaching-admin/admissions/tests/${result.testId}`);
        } else {
          loadTestConfiguration();
          loadRegistrations();
        }
      } else {
        toast.error('Failed to save: ' + result.error);
      }
    } catch (error) {
      toast.error('Error saving configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdmitCardUpload = async (registrationId, file) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    try {
      const result = await admissionsService.uploadAdmitCard(testId, registrationId, file);
      
      if (result.success) {
        toast.success('Admit card uploaded successfully');
        loadRegistrations();
      } else {
        toast.error('Failed to upload admit card');
      }
    } catch (error) {
      toast.error('Error uploading admit card: ' + error.message);
    }
  };

  const handleTestResultUpdate = async (registrationId, resultStatus) => {
    try {
      const result = await admissionsService.updateTestResult(registrationId, resultStatus);
      
      if (result.success) {
        toast.success(`Test result updated to ${resultStatus === 'selected' ? 'Selected' : resultStatus === 'rejected' ? 'Rejected' : 'Pending'}`);
        loadRegistrations();
      } else {
        toast.error('Failed to update test result');
      }
    } catch (error) {
      toast.error('Error updating test result: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/coaching-admin/admissions/tests')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {isNewTest ? 'Create New Test' : 'Edit Admission Test'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {isNewTest ? 'Configure a new admission test' : 'Manage test configuration and registrations'}
          </p>
        </div>
      </div>

      {/* Test Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Test Configuration</h2>
          
          {/* Enable/Disable Test */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={testData.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                className="w-5 h-5 mt-1"
              />
              <div className="flex-1">
                <label className="text-sm font-semibold text-gray-900 dark:text-white">
                  Enable Test
                </label>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  When enabled, students will see this test on your institution page
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Deadline Passed Notice */}
            {testData.lastDateToRegister && new Date(testData.lastDateToRegister) < new Date() && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-300 dark:border-red-700">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                      ⚠️ Registration Deadline Passed
                    </p>
                    <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                      The last date to register ({new Date(testData.lastDateToRegister).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}) has passed. Students can no longer register for this test. Update the last date and save to re-enable registrations.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Mandatory Toggle */}
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={testData.mandatory}
                  onChange={(e) => handleInputChange('mandatory', e.target.checked)}
                  className="w-5 h-5 mt-1"
                />
                <div className="flex-1">
                  <label className="text-sm font-semibold text-gray-900 dark:text-white">
                    Make Test Mandatory
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    If mandatory, students must register for this test before applying for admission
                  </p>
                </div>
              </div>
            </div>

            {/* Registration Status */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={testData.registrationOpen}
                  onChange={(e) => handleInputChange('registrationOpen', e.target.checked)}
                  className="w-5 h-5 mt-1"
                />
                <div className="flex-1">
                  <label className="text-sm font-semibold text-gray-900 dark:text-white">
                    Registration Open
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Temporarily close/open test registrations without disabling the entire test
                  </p>
                </div>
              </div>
            </div>

            {/* Test Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Test Name *
                </label>
                <input
                  type="text"
                  value={testData.testName}
                  onChange={(e) => handleInputChange('testName', e.target.value)}
                  placeholder="e.g., Entrance Examination 2024"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Registration Fee (₹)
                </label>
                <input
                  type="number"
                  value={testData.registrationFee}
                  onChange={(e) => handleInputChange('registrationFee', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Test Date *
                </label>
                <input
                  type="date"
                  value={testData.testDate}
                  onChange={(e) => handleInputChange('testDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Test Time
                </label>
                <input
                  type="time"
                  value={testData.testTime}
                  onChange={(e) => handleInputChange('testTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Duration
                </label>
                <input
                  type="text"
                  value={testData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  placeholder="e.g., 2 hours"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Date to Register
                </label>
                <input
                  type="date"
                  value={testData.lastDateToRegister}
                  onChange={(e) => handleInputChange('lastDateToRegister', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Total Marks
                </label>
                <input
                  type="text"
                  value={testData.totalMarks}
                  onChange={(e) => handleInputChange('totalMarks', e.target.value)}
                  placeholder="e.g., 100"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Passing Marks
                </label>
                <input
                  type="text"
                  value={testData.passingMarks}
                  onChange={(e) => handleInputChange('passingMarks', e.target.value)}
                  placeholder="e.g., 40"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Syllabus
              </label>
              <textarea
                value={testData.syllabus}
                onChange={(e) => handleInputChange('syllabus', e.target.value)}
                rows={4}
                placeholder="Enter syllabus details..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Exam Pattern
              </label>
              <textarea
                value={testData.examPattern}
                onChange={(e) => handleInputChange('examPattern', e.target.value)}
                rows={4}
                placeholder="e.g., Multiple Choice Questions, Descriptive Questions, etc."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Instructions
              </label>
              <textarea
                value={testData.instructions}
                onChange={(e) => handleInputChange('instructions', e.target.value)}
                rows={4}
                placeholder="Enter important instructions for candidates..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isNewTest ? 'Create Test' : 'Save Changes'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Test Registrations */}
      {!isNewTest && testData.enabled && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Test Registrations ({registrations.length})
            </h2>
            <button
              onClick={() => {
                console.log('Manual refresh clicked for testId:', testId);
                loadRegistrations();
              }}
              disabled={loadingRegistrations}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingRegistrations ? 'Loading...' : 'Refresh Registrations'}
            </button>
          </div>

          {loadingRegistrations ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 dark:text-gray-400 mt-4">Loading registrations...</p>
            </div>
          ) : registrations.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No registrations yet</p>
              <p className="text-sm mt-2">Students will appear here once they register for this test</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Student Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Registered On
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Admit Card
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Test Result
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Application Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {registrations.map((registration) => (
                    <tr key={registration.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {registration.studentName || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {registration.email || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {registration.phone || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {registration.registeredAt?.toDate?.().toLocaleDateString() || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {registration.admitCardUrl ? (
                          <a
                            href={registration.admitCardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <Download className="w-4 h-4" />
                            View
                          </a>
                        ) : (
                          <label className="cursor-pointer text-gray-500 hover:text-gray-700 flex items-center gap-1">
                            <Upload className="w-4 h-4" />
                            Upload
                            <input
                              type="file"
                              accept="application/pdf"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleAdmitCardUpload(registration.id, e.target.files[0]);
                                }
                              }}
                            />
                          </label>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <select
                          value={registration.testResult || ''}
                          onChange={(e) => handleTestResultUpdate(registration.id, e.target.value)}
                          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                          <option value="">Pending</option>
                          <option value="selected">Selected</option>
                          <option value="rejected">Rejected</option>
                        </select>
                        {registration.testResult && (
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                            registration.testResult === 'selected'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {registration.testResult === 'selected' ? '✓ Selected' : '✗ Rejected'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          registration.hasApplied
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {registration.hasApplied ? 'Applied' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdmissionTestEdit;
